import { type MeResponse, NOC_STAGES } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type CSSProperties, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import mascot from "../../assets/brand/dashboard-mascot-wave.webp";
import { gamesDetailOptions, useDefaultGamesId } from "../../lib/competition/games";
import { SECTOR_DASHBOARDS, type SectorNumber, hasSectorDashboards, sectorOpenFor } from "../../lib/dashboard/sectors";
import { ensureLocaleFont } from "../../lib/i18n/fonts";
import { roleLabel } from "../../lib/users/labels";
import { LockIcon } from "../accreditation/icons";
import { NocFlag } from "../common/noc-flag";
import { CheckIcon, ShieldIcon } from "./icons";
import { CONFETTI_COUNT, confettiStyle } from "./welcome/confetti";
import { Landmark } from "./welcome/landmarks";
import { HOST_THEME, type NocTheme, nocThemeFor } from "./welcome/themes";
import "./welcome/welcome.css";

/**
 * The welcome banner above every dashboard (user, 2026-10-31: "every
 * dashboard must feel welcoming based on what NOC is logged in"; Notion:
 * "Welcome NOC Brunei … Wayne Leong (MASOC Official)", "show the flag if
 * from a NOC").
 *
 * Who: a NOC session (ORGANIZATION, its group naming ONE organisation —
 * `/auth/me.group.organization`) is welcomed by name with its flag, in its
 * colours, in front of its landmark, and the mascot says hello in its
 * language (`welcome/themes.ts`). Everyone else gets the host's banner —
 * Malaysia — and an ADMIN reads as a MASOC official (the role menu's "MASOC").
 *
 * `sector` (a sector's dashboard): the sector's name and the NOC's road
 * through the four sectors — reached ones link to their dashboards, the rest
 * show a lock (`/auth/me.noc_stage`; never re-derived). The admins see the
 * same road, all open.
 *
 * Only what we know is shown: the days-to-Games countdown reads the main
 * games' `starts_on` and is left out while it is not set. Every moving part
 * is CSS (welcome.css) and stops under `prefers-reduced-motion`.
 */
export function DashboardHero({ session, sector }: { session: MeResponse; sector?: SectorNumber }) {
	const { t } = useTranslation("dashboard");
	const { t: tUsers } = useTranslation(["users", "common"]);
	const role = session.active_role;
	const group = session.group && session.group.is_active && session.group.role_code === role ? session.group : null;
	const noc = role === "ORGANIZATION" ? (group?.organization ?? null) : null;
	const theme: NocTheme = (noc && nocThemeFor(noc.code)) || HOST_THEME;
	const days = useDaysToGames();

	useEffect(() => {
		if (theme.font) ensureLocaleFont(theme.font);
	}, [theme.font]);

	const vars = {
		"--wb-ink": theme.ink,
		"--wb-sun": theme.sun,
		"--wb-gold": theme.gold,
		"--wb-r1": theme.ribbon[0],
		"--wb-r2": theme.ribbon[1],
		"--wb-r3": theme.ribbon[2],
	} as CSSProperties;

	const chip = noc ? (
		<span className="wb__chip wb__chip--noc">
			<NocFlag code={noc.code} size="sm" />
			{noc.name}
		</span>
	) : role ? (
		<span className="wb__chip">
			<ShieldIcon />
			{role === "ADMIN" ? t("hero.hostOfficial") : roleLabel(tUsers, role)}
		</span>
	) : null;
	// A scoped login's group says where it works (OPERATION: "Transport desk"); a NOC's group is its NOC, already in the chip.
	const where = !noc && group ? group.name : null;
	const countdown = days === null ? null : days === 0 ? t("hero.gamesToday") : t("hero.daysToGames", { count: days });

	return (
		<section className="wb" style={vars} data-landmark={theme.landmark}>
			<span className="wb__dots" aria-hidden="true" />
			<span className="wb__confetti" aria-hidden="true">
				{Array.from({ length: CONFETTI_COUNT }, (_, i) => (
					<i key={i} style={confettiStyle(i)} />
				))}
			</span>
			<div className="wb__art" aria-hidden="true">
				<Landmark name={theme.landmark} />
				<div className="wb__mascot">
					<img src={mascot} alt="" width={372} height={560} decoding="async" />
					<Bubble greetings={theme.greeting} />
				</div>
			</div>
			<Ribbon />

			<div className="wb__t">
				<p className="wb__k">
					{noc && <NocFlag code={noc.code} size="lg" className="wb__flag" />}
					<span>{noc ? t("hero.welcomeNoc", { noc: noc.name }) : t("hero.welcome")}</span>
				</p>
				<h1 className="wb__name">{session.user.name}</h1>
				<p className="wb__meta">
					{chip}
					{[where, countdown].filter(Boolean).length > 0 && <span>{[where, countdown].filter(Boolean).join(" · ")}</span>}
				</p>
				{sector !== undefined && hasSectorDashboards(session) && <SectorRoad session={session} current={sector} />}
			</div>
		</section>
	);
}

/** The mascot's speech bubble: the NOC's own greeting, cycling when it has several. */
function Bubble({ greetings }: { greetings: NocTheme["greeting"] }) {
	const [i, setI] = useState(0);
	useEffect(() => {
		setI(0);
		if (greetings.length < 2 || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
		const id = window.setInterval(() => setI((n) => (n + 1) % greetings.length), 2800);
		return () => window.clearInterval(id);
	}, [greetings]);
	const g = greetings[i] ?? greetings[0];
	if (!g) return null;
	return (
		<span className="wb__bubble">
			<span key={g.text} lang={g.lang}>
				{g.text}
			</span>
		</span>
	);
}

/** Three bands in the flag's colours, flowing along the bottom edge. */
function Ribbon() {
	// A wave of period 360 drawn across 2 × 1440 units; each band slides one period (welcome.css) and repeats.
	const wave = (y: number, amp: number) => {
		let d = `M0 ${y}`;
		for (let x = 0; x < 2880; x += 360) d += ` Q${x + 90} ${y - amp} ${x + 180} ${y} T${x + 360} ${y}`;
		return `${d} V60 H0 Z`;
	};
	return (
		<svg className="wb__ribbon" viewBox="0 0 1440 60" preserveAspectRatio="none" aria-hidden="true" focusable="false">
			<path className="wb__wave wb__wave--1" d={wave(22, 12)} fill="var(--wb-r1)" />
			<path className="wb__wave wb__wave--2" d={wave(34, 10)} fill="var(--wb-r2)" />
			<path className="wb__wave wb__wave--3" d={wave(46, 8)} fill="var(--wb-r3)" />
		</svg>
	);
}

/** Sector N · its name, and the four sectors as a road: reached ones link to their dashboards. */
function SectorRoad({ session, current }: { session: MeResponse; current: SectorNumber }) {
	const { t } = useTranslation("dashboard");
	const { t: tStages } = useTranslation("nocStages");
	const stage = NOC_STAGES.find((s) => s.stage === current);
	return (
		<nav className="wb__road" aria-label={t("hero.road")}>
			<span className="wb__road-l">
				<b>{tStages("stageLabel", { n: current })}</b>
				{stage && <span>{tStages(`stageName.${stage.key}`)}</span>}
			</span>
			<ol>
				{NOC_STAGES.map(({ stage: n, key }) => {
					const open = sectorOpenFor(session, n);
					const here = n === current;
					const name = `${tStages("stageLabel", { n })} · ${tStages(`stageName.${key}`)}`;
					const label = here ? t("hero.roadHere", { sector: name }) : open ? name : t("hero.roadLocked", { sector: name });
					const mark = here ? n : open ? <CheckIcon /> : <LockIcon />;
					return (
						<li key={n} data-state={here ? "here" : open ? "open" : "locked"}>
							{open && !here ? (
								<Link to={SECTOR_DASHBOARDS[n]} aria-label={label} title={label}>
									{mark}
								</Link>
							) : (
								<span role="img" aria-label={label} title={label} aria-current={here ? "page" : undefined}>
									{mark}
								</span>
							)}
						</li>
					);
				})}
			</ol>
		</nav>
	);
}

/** Whole days from today (in the games' own time zone) to `starts_on`; null when unknown or past. */
function useDaysToGames(): number | null {
	const gamesId = useDefaultGamesId();
	const games = useQuery({ ...gamesDetailOptions(gamesId ?? ""), enabled: !!gamesId });
	const startsOn = games.data?.starts_on;
	if (!startsOn || !games.data) return null;
	// "YYYY-MM-DD" today in the games' zone. en-CA is a fixed technical format here, not the viewer's locale.
	const today = new Intl.DateTimeFormat("en-CA", { timeZone: games.data.timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
	const days = Math.round((Date.parse(`${startsOn}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
	return Number.isFinite(days) && days >= 0 ? days : null;
}
